/**
 * CampusGroupedList — Shared Craigslist-style campus picker list
 * ───────────────────────────────────────────────────────────────
 * Used by CampusPicker (registration) and CampusSelector (map nav).
 *
 * Features:
 *  - "Universal" row pinned at top
 *  - Alphabetical region headers, campuses alphabetical within
 *  - Regions with >10 schools collapse by default
 *  - Live search (debounced 250ms) via /api/campuses/search
 *  - "Don't see your school? Add it" self-serve request flow
 *
 * Place at: src/components/CampusGroupedList.jsx
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, ChevronDown, ChevronRight, MapPin, Search, Plus, Check, Loader2, X } from 'lucide-react';
import { campusAPI } from '../api/campusAPI';
import { cn } from '../utils/cn';

// ─── Constants ───────────────────────────────────────────────────────────────

const COLLAPSE_THRESHOLD = 10;   // regions with more schools than this collapse by default
const SEARCH_DEBOUNCE_MS = 250;

// ─── Request-a-school inline form ────────────────────────────────────────────

const RequestSchoolForm = ({ onSubmitted, onCancel }) => {
  const [form, setForm]       = useState({ name: '', city: '', state: '', country: 'United States', region: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('School name is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await campusAPI.requestCampus(form);
      setDone(true);
      setTimeout(() => onSubmitted?.(), 1800);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Submission failed. Please try again.';
      // If it already exists, surface the campus id so caller can select it
      if (err?.response?.data?.campusId) {
        onSubmitted?.(err.response.data.campusId);
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="px-4 py-4 text-center">
        <Check className="w-5 h-5 text-emerald-400 mx-auto mb-1.5" />
        <p className="text-sm text-zinc-300 font-medium">Request submitted!</p>
        <p className="text-xs text-zinc-500 mt-0.5">We'll review it shortly.</p>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 border-t border-zinc-800 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Add your school</p>
        <button type="button" onClick={onCancel} className="text-zinc-600 hover:text-zinc-400">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <input
        type="text"
        placeholder="School name *"
        value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        maxLength={200}
        className="w-full px-3 py-2 text-sm bg-zinc-800 border border-zinc-700/60 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="City"
          value={form.city}
          onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
          maxLength={100}
          className="px-3 py-2 text-sm bg-zinc-800 border border-zinc-700/60 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60"
        />
        <input
          type="text"
          placeholder="State / Province"
          value={form.state}
          onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
          maxLength={50}
          className="px-3 py-2 text-sm bg-zinc-800 border border-zinc-700/60 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60"
        />
      </div>
      <input
        type="text"
        placeholder="Country"
        value={form.country}
        onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
        maxLength={100}
        className="w-full px-3 py-2 text-sm bg-zinc-800 border border-zinc-700/60 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60"
      />
      <input
        type="text"
        placeholder="Region (e.g. California — Bay Area)"
        value={form.region}
        onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
        maxLength={150}
        className="w-full px-3 py-2 text-sm bg-zinc-800 border border-zinc-700/60 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60"
      />
      {error && <p className="text-xs text-red-400">{error}</p>}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {submitting ? 'Submitting...' : 'Submit request'}
      </button>
    </div>
  );
};

// ─── Campus row ──────────────────────────────────────────────────────────────

const CampusRow = ({ campus, isSelected, isMine, onClick, showRegion = false }) => (
  <button
    type="button"
    onClick={() => onClick(campus.id)}
    className={cn(
      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150',
      isSelected
        ? 'bg-zinc-800 text-white'
        : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200',
    )}
  >
    <div
      className="w-3 h-3 rounded-full flex-shrink-0"
      style={{ backgroundColor: campus.color }}
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm font-medium truncate">{campus.name}</span>
        {isMine && (
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 bg-zinc-800/80 px-1.5 py-0.5 rounded flex-shrink-0">
            Home
          </span>
        )}
        {campus.pending && (
          <span className="text-[9px] uppercase tracking-wider text-amber-600 bg-amber-900/30 px-1.5 py-0.5 rounded flex-shrink-0">
            Pending
          </span>
        )}
      </div>
      {showRegion && campus.region && (
        <p className="text-[10px] text-zinc-600 truncate">{campus.region}</p>
      )}
      {campus.locationCity && (
        <p className="text-[10px] text-zinc-600 flex items-center gap-1 mt-0.5">
          <MapPin className="w-2 h-2 flex-shrink-0" />
          {[campus.locationCity, campus.locationState].filter(Boolean).join(', ')}
        </p>
      )}
    </div>
    {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
  </button>
);

// ─── Region section ──────────────────────────────────────────────────────────

const RegionSection = ({ region, campuses, selectedId, myCampusId, onSelect, defaultOpen }) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const collapsible = campuses.length > COLLAPSE_THRESHOLD;

  return (
    <div>
      {/* Region header */}
      <button
        type="button"
        onClick={() => collapsible && setExpanded(e => !e)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-1.5',
          collapsible ? 'cursor-pointer hover:bg-zinc-800/30' : 'cursor-default',
        )}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          {region}
        </span>
        {collapsible && (
          expanded
            ? <ChevronDown className="w-3 h-3 text-zinc-600" />
            : <ChevronRight className="w-3 h-3 text-zinc-600" />
        )}
      </button>

      {/* Campus rows */}
      {expanded && (
        <div className="px-1 pb-1">
          {campuses.map(campus => (
            <CampusRow
              key={campus.id}
              campus={campus}
              isSelected={campus.id === selectedId}
              isMine={campus.id === myCampusId}
              onClick={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {number|null} props.selectedId     — currently selected campus id (null = Universal)
 * @param {number|null} props.myCampusId     — the user's home campus id (for "Home" badge)
 * @param {boolean}  props.showUniversal     — show the Universal / Worldwide row (default true)
 * @param {boolean}  props.isWorldwide       — whether worldwide mode is active (for checkmark)
 * @param {Function} props.onSelect          — (campusId: number|null) => void
 * @param {string}   props.universalLabel    — label for the universal row (default "Universal")
 * @param {string}   props.universalSub      — sublabel for the universal row
 * @param {boolean}  props.showRequestForm   — show "Add my school" footer (default true)
 * @param {string}   props.maxHeight         — CSS max-height for the scroll container
 */
const CampusGroupedList = ({
  selectedId = null,
  myCampusId = null,
  showUniversal = true,
  isWorldwide = false,
  onSelect,
  universalLabel = 'Universal',
  universalSub = 'All campuses, all connections',
  showRequestForm = true,
  maxHeight = '320px',
}) => {
  const [regions, setRegions]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchQuery, setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null);   // null = not searching
  const [searching, setSearching]       = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const searchRef                       = useRef(null);
  const debounceRef                     = useRef(null);

  // Load grouped list on mount
  useEffect(() => {
    campusAPI.grouped()
      .then(res => setRegions(res.data?.regions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(debounceRef.current);

    if (!q.trim()) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await campusAPI.search(q);
        setSearchResults(res.data?.campuses || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearching(false);
    searchRef.current?.focus();
  };

  const handleSelect = (campusId) => {
    onSelect?.(campusId);
  };

  const handleUniversal = () => {
    onSelect?.(null);
  };

  const handleFormSubmitted = (campusId) => {
    setShowForm(false);
    if (campusId) {
      // School already existed — select it
      handleSelect(campusId);
    }
    // Otherwise reload grouped list to pick up the pending campus
    campusAPI.grouped()
      .then(res => setRegions(res.data?.regions || []))
      .catch(() => {});
  };

  const isUniversalSelected = selectedId === null;

  return (
    <div className="flex flex-col">
      {/* Search input */}
      <div className="px-2 pt-2 pb-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search schools..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-zinc-800/80 border border-zinc-700/50 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable list */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {/* Universal row — always first */}
        {showUniversal && (
          <div className="px-2 pt-1 pb-1">
            <button
              type="button"
              onClick={handleUniversal}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150',
                isUniversalSelected || isWorldwide
                  ? 'bg-indigo-500/15 text-indigo-300'
                  : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200',
              )}
            >
              <Globe className={cn(
                'w-4 h-4 flex-shrink-0',
                isUniversalSelected || isWorldwide ? 'text-indigo-400' : 'text-zinc-500',
              )} />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{universalLabel}</span>
                <p className="text-[10px] text-zinc-600">{universalSub}</p>
              </div>
              {(isUniversalSelected || isWorldwide) && (
                <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
              )}
            </button>
          </div>
        )}

        {/* Divider between Universal and school list */}
        {showUniversal && <div className="h-px bg-zinc-800 mx-3 mb-1" />}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8 text-zinc-600">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm">Loading schools...</span>
          </div>
        )}

        {/* Search results */}
        {!loading && searchResults !== null && (
          <div className="px-1 pb-1">
            {searching && (
              <div className="flex items-center gap-2 px-3 py-2 text-zinc-600 text-xs">
                <Loader2 className="w-3 h-3 animate-spin" />
                Searching...
              </div>
            )}
            {!searching && searchResults.length === 0 && (
              <p className="px-3 py-3 text-xs text-zinc-600 text-center">
                No schools found for "{searchQuery}"
              </p>
            )}
            {!searching && searchResults.map(campus => (
              <div key={campus.id} className="px-1">
                <CampusRow
                  campus={campus}
                  isSelected={campus.id === selectedId}
                  isMine={campus.id === myCampusId}
                  onClick={handleSelect}
                  showRegion
                />
              </div>
            ))}
          </div>
        )}

        {/* Grouped regions (shown when not searching) */}
        {!loading && searchResults === null && regions.map((regionObj) => (
          <RegionSection
            key={regionObj.name}
            region={regionObj.name}
            campuses={regionObj.campuses}
            selectedId={selectedId}
            myCampusId={myCampusId}
            onSelect={handleSelect}
            defaultOpen={regionObj.campuses.length <= COLLAPSE_THRESHOLD}
          />
        ))}

        {/* Empty state — no schools at all */}
        {!loading && searchResults === null && regions.length === 0 && (
          <p className="px-4 py-4 text-xs text-zinc-600 text-center">
            No schools yet — be the first to add yours!
          </p>
        )}
      </div>

      {/* Add my school footer */}
      {showRequestForm && !showForm && (
        <div className="border-t border-zinc-800">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-600 hover:text-zinc-400 hover:bg-zinc-800/40 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Don't see your school? Add it
          </button>
        </div>
      )}

      {showRequestForm && showForm && (
        <RequestSchoolForm
          onSubmitted={handleFormSubmitted}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default CampusGroupedList;